import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { FormTemplate, FormSection } from "../types/forms";
import { getConsultationById } from "../services/consultationform";
import UserPreview from "./forms/[id]/builder/UserPreview";
import { Loader2, X, FileText, ArrowLeft, Phone, Mail, Calendar, User } from "lucide-react";
import { ConsultationDetail } from "../types/consultation";

const mapSectionType = (section: any): FormSection => {
    if (section.type) return section as FormSection;

    const templateIdMap: Record<number, FormSection["type"]> = {
        1: "contact",
        2: "medical",
        3: "marketing",
        4: "question",
        5: "heading",
        6: "text",
        7: "policy",
        8: "disclaimer",
    };

    return {
        ...section,
        type: section.form_template_id ? templateIdMap[section.form_template_id] : null
    } as FormSection;
};

const deriveSectionTitle = (s: FormSection): string | null => {
    let sectionTitle = s.title ?? null;
    if ((s.type === "text" || s.type === "heading") && !sectionTitle?.trim() && (s.fields?.length ?? 0) > 0) {
        const first = s.fields![0];
        if (typeof first.options === "string" && first.options.trim()) sectionTitle = first.options.trim();
        else if (first.label?.trim()) sectionTitle = first.label.trim();
        else if (first.options && typeof first.options === "object" && first.options !== null && "content" in first.options) {
            const opts = first.options as { content?: string };
            if (opts.content?.trim()) sectionTitle = opts.content.trim();
        }
    }
    return sectionTitle;
};

export default function ConsultationResponsePage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [template, setTemplate] = useState<FormTemplate | null>(null);
    const [consultation, setConsultation] = useState<ConsultationDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [formValues, setFormValues] = useState<Record<string, any>>({});

    useEffect(() => {
        if (!id) return;

        const fetchData = async () => {
            try {
                setLoading(true);
                const res = await getConsultationById(id);

                if (res?.form) {
                    const consultData = res.form;
                    setConsultation(consultData);

                    // Map Sections
                    const mappedForm: FormTemplate = {
                        ...consultData.form,
                        id: String(consultData.form.id),
                        uuid: consultData.form.uuid || "",
                        services: (consultData.form as any).business_services || [],
                        saved: true,
                        draft: false,
                        fields: (consultData.form as any).fields || [],
                        sections: (consultData.form.sections || [])
                            .map((s) => {
                                const mapped = mapSectionType(s);
                                return { ...mapped, title: deriveSectionTitle(mapped) ?? mapped.title };
                            })
                            .sort((a: FormSection, b: FormSection) => (a.order || 0) - (b.order || 0))
                    };
                    setTemplate(mappedForm);

                    // Map Answers to formValues
                    const answersMap: Record<string | number, any> = {};
                    consultData.fields.forEach(f => {
                        let parsedAnswer = f.value;
                        try {
                            // Check if it's a JSON string (for checkbox groups / medical conditions)
                            if (typeof f.value === 'string' && (f.value.startsWith('{') || f.value.startsWith('['))) {
                                parsedAnswer = JSON.parse(f.value);
                            }
                        } catch (e) {
                            // Not JSON, keep as string
                        }
                        answersMap[f.field_id] = parsedAnswer;
                    });

                    const mappedValues: Record<string | number, any> = {
                        signature: consultData.signature
                    };

                    mappedForm.sections.forEach(section => {
                        const sectionValues: Record<string | number, any> = {};
                        section.fields?.forEach(field => {
                            if (answersMap[field.id] !== undefined) {
                                sectionValues[field.id] = answersMap[field.id];
                            }
                        });

                        if (Object.keys(sectionValues).length > 0) {
                            mappedValues[section.id] = sectionValues;
                        }
                    });

                    setFormValues(mappedValues);
                } else {
                    setError("Consultation response not found");
                }
            } catch (err: any) {
                setError(err.message || "Failed to load consultation response");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen text-gray-500 bg-gray-50">
                <Loader2 className="w-10 h-10 animate-spin mb-4 text-[#885ABB]" />
                <p className="animate-pulse text-lg font-medium">Fetching response data...</p>
            </div>
        );
    }

    if (error || !template) {
        return (
            <div className="flex items-center justify-center min-h-screen p-8 text-center bg-gray-50">
                <div className="bg-white p-10 rounded-3xl shadow-2xl border border-red-50 max-w-md w-full">
                    <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <X className="w-10 h-10 text-red-500" />
                    </div>
                    <h2 className="text-2xl font-bold mb-3 text-gray-900">Unable to load</h2>
                    <p className="text-gray-600 mb-8">{error || "Response could not be loaded."}</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="w-full py-3 px-6 bg-gray-900 text-white rounded-xl font-semibold hover:bg-black transition-all flex items-center justify-center gap-2"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Top Navigation */}
            <div className="bg-white border-b border-gray-100 sticky top-0 z-10 shadow-sm">
                <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-gray-600 hover:text-[#885ABB] font-medium transition-colors group"
                    >
                        <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center group-hover:bg-purple-50 transition-colors">
                            <ArrowLeft className="w-4 h-4" />
                        </div>
                        Back to List
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="bg-purple-50 text-[#885ABB] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border border-purple-100">
                            Consultation Response
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 pt-10 space-y-8">
                {/* Header Section */}
                <div className="bg-white rounded-[2rem] shadow-[0_8px_40px_rgba(0,0,0,0.04)] border border-gray-100 overflow-hidden">
                    <div className="bg-[#885ABB] h-2 w-full" />
                    <div className="p-8 md:p-10">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                            <div className="space-y-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center border border-purple-100 shadow-sm">
                                        <User className="text-[#885ABB] w-8 h-8" />
                                    </div>
                                    <div>
                                        <h1 className="text-3xl font-black text-gray-900 tracking-tight">
                                            {consultation?.user.name}
                                        </h1>
                                        <div className="flex items-center gap-2 text-gray-500 mt-1">
                                            <Calendar className="w-4 h-4" />
                                            <span className="text-sm font-medium">Submitted {new Date(consultation?.submitted_at || consultation?.created_at || "").toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-3 md:min-w-[280px]">
                                <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-2xl border border-gray-100">
                                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border border-gray-100 shadow-sm">
                                        <Mail className="w-4 h-4 text-gray-400" />
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">Email Address</span>
                                        <span className="text-sm font-semibold text-gray-700">{consultation?.user.email}</span>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-2xl border border-gray-100">
                                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border border-gray-100 shadow-sm">
                                        <Phone className="w-4 h-4 text-gray-400" />
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">Mobile Number</span>
                                        <span className="text-sm font-semibold text-gray-700">{(consultation?.user as any).mobile || 'N/A'}</span>
                                    </div>
                                </div>
                                {(consultation?.user as any).landline && (
                                    <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-2xl border border-gray-100">
                                        <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center border border-gray-100 shadow-sm">
                                            <Phone className="w-4 h-4 text-gray-400 opacity-60" />
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">Landline Number</span>
                                            <span className="text-sm font-semibold text-gray-700">{(consultation?.user as any).landline}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Form Content */}
                <div className="bg-white rounded-[2rem] shadow-[0_8px_40px_rgba(0,0,0,0.04)] border border-gray-100 overflow-hidden">
                    <UserPreview
                        template={template}
                        formValues={formValues}
                        showSubmit={false}
                        readOnly={true}
                    />
                </div>
            </div>
        </div>
    );
}
